import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errText } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Facebook, Twitter, Linkedin, AtSign, Plug, Unplug, Loader2, Cable, ChevronDown, ChevronUp, RefreshCw } from "lucide-react";

const FIELDS = {
  meta: [
    { key: "page_token", label: "Meta access token — a User token imports ALL your pages at once", secret: true },
    { key: "page_id", label: "Page ID (optional — only if pasting a single Page token)" },
  ],
  x: [
    { key: "consumer_key", label: "API Key" },
    { key: "consumer_secret", label: "API Key Secret", secret: true },
    { key: "access_token", label: "Access Token" },
    { key: "access_token_secret", label: "Access Token Secret", secret: true },
  ],
  linkedin: [
    { key: "access_token", label: "Access token (w_member_social scope)", secret: true },
    { key: "author_urn", label: "Author URN (optional, e.g. urn:li:person:AbC123)" },
  ],
  threads: [
    { key: "access_token", label: "Long-lived Threads access token", secret: true },
  ],
};

const HELP = {
  meta: "developers.facebook.com → create an app (stay in dev mode) → Graph API Explorer → grant pages_manage_posts, pages_read_engagement, instagram_content_publish → GET me/accounts to copy your Page ID + Page token. Covers Facebook AND Instagram.",
  x: "developer.x.com portal → your app → 'Keys and tokens' → set app permissions to Read & Write, then copy the API key/secret and generate the Access token/secret.",
  linkedin: "linkedin.com/developers → your app → 'Token generator' tool → generate a token with w_member_social (add openid + profile so we can auto-detect your URN).",
  threads: "developers.facebook.com → your app → Add Product → Threads API → Threads Access Token Tool → grant threads_basic, threads_content_publish, threads_manage_replies → generate a long-lived token (60-day) and paste it here. We'll auto-derive the user ID from the token.",
};

const META_INFO = {
  meta: { label: "Facebook + Instagram", Icon: Facebook },
  x: { label: "X / Twitter", Icon: Twitter },
  linkedin: { label: "LinkedIn", Icon: Linkedin },
  threads: { label: "Threads", Icon: AtSign },
};

const ProviderBlock = ({ prov, status, onChanged }) => {
  const { label, Icon } = META_INFO[prov];
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/integrations/native/${prov}`, form);
      toast.success(`${label} connected — ${data.detail}`);
      setForm({});
      setOpen(false);
      onChanged();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const disconnect = async () => {
    await api.delete(`/integrations/native/${prov}`);
    toast.success(`${label} disconnected`);
    onChanged();
  };
  const redetect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/integrations/native/meta/refresh");
      const igCount = data.pages.filter((p) => p.ig).length;
      toast.success(`Re-checked ${data.pages.length} pages — ${igCount} with Instagram linked${data.errors?.length ? ` · ${data.errors.length} error(s)` : ""}`);
      onChanged();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const verifyThreads = async () => {
    setVerifying(true);
    setVerifyResult(null);
    try {
      const { data } = await api.post("/integrations/native/threads/publish-test");
      setVerifyResult({ ok: true, id: data.external_id });
      toast.success(`Threads publish verified — thread ${data.external_id}`);
      onChanged();
    } catch (e) { setVerifyResult({ ok: false, msg: errText(e) }); toast.error(errText(e)); }
    finally { setVerifying(false); }
  };

  return (
    <div className={`p-4 rounded-xl border ${status?.connected ? "border-accent bg-secondary/40" : "border-border bg-muted/40"}`} data-testid={`native-${prov}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className="flex items-center gap-2.5 text-sm font-semibold"><Icon size={17} /> {label}
          {status?.connected && <Badge className="bg-accent text-accent-foreground border-0 ml-1">Live{prov !== "meta" ? ` · ${status.detail}` : ""}</Badge>}
        </span>
        {status?.connected ? (
          <div className="flex gap-2">
            {prov === "meta" && (
              <Button size="sm" variant="outline" data-testid="native-meta-redetect" disabled={busy} onClick={redetect}>
                {busy ? <Loader2 size={12} className="mr-1 animate-spin" /> : <RefreshCw size={12} className="mr-1" />} Re-detect Instagram
              </Button>
            )}
            {prov === "threads" && (
              <Button size="sm" variant="outline" data-testid="native-threads-verify" disabled={verifying} onClick={verifyThreads}>
                {verifying ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Plug size={12} className="mr-1" />} Verify publish
              </Button>
            )}
            <Button size="sm" variant="outline" data-testid={`native-disconnect-${prov}`} onClick={disconnect}><Unplug size={12} className="mr-1" /> Disconnect</Button>
          </div>
        ) : (
          <Button size="sm" variant="outline" data-testid={`native-expand-${prov}`} onClick={() => setOpen(!open)}>
            {open ? <ChevronUp size={12} className="mr-1" /> : <ChevronDown size={12} className="mr-1" />} Set up
          </Button>
        )}
      </div>
      {status?.connected && prov === "meta" && (status.pages || []).length > 0 && (
        <div className="mt-3 space-y-1.5" data-testid="native-meta-pages">
          {status.pages.map((pg) => (
            <div key={pg.name} className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
              <span className="font-semibold text-foreground">{pg.name}</span>
              {pg.brand ? <Badge variant="secondary" className="text-[9px]">→ {pg.brand}</Badge> : <Badge variant="outline" className="text-[9px]">no brand match</Badge>}
              <Badge variant="outline" className={`text-[9px] ${pg.ig ? "text-accent border-accent/50" : ""}`}>{pg.ig ? "IG linked" : "FB only"}</Badge>
            </div>
          ))}
        </div>
      )}
      {status?.connected && prov === "threads" && (verifyResult || status?.last_publish_test_at) && (
        <div className="mt-3 text-xs flex items-center gap-2 flex-wrap" data-testid="native-threads-verify-status">
          {verifyResult?.ok && (
            <Badge className="bg-accent text-accent-foreground border-0 text-[10px]">Verified · thread {verifyResult.id}</Badge>
          )}
          {verifyResult && !verifyResult.ok && (
            <Badge variant="destructive" className="text-[10px]">Verify failed</Badge>
          )}
          {!verifyResult && status?.last_publish_test_at && (
            <span className="text-muted-foreground">Last verified: {new Date(status.last_publish_test_at).toLocaleString()} · id {status.last_publish_test_id}</span>
          )}
        </div>
      )}
      {open && !status?.connected && (
        <div className="mt-4 space-y-2.5">
          <p className="text-xs text-muted-foreground">{HELP[prov]}</p>
          {FIELDS[prov].map((f) => (
            <Input key={f.key} data-testid={`native-${prov}-${f.key}`} type={f.secret ? "password" : "text"} placeholder={f.label}
              value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
          ))}
          <Button size="sm" data-testid={`native-connect-${prov}`} onClick={connect} disabled={busy}>
            {busy ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Plug size={12} className="mr-1" />} Verify & connect
          </Button>
        </div>
      )}
    </div>
  );
};

export default function NativeConnections() {
  const [status, setStatus] = useState(null);
  const load = () => api.get("/integrations/native").then((r) => setStatus(r.data));
  useEffect(() => { load(); }, []);

  return (
    <Card data-testid="native-connections-card">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2"><Cable size={18} /> Native platform connections <Badge variant="secondary" className="font-sans font-normal">free — no middleman</Badge></CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">Connect directly to each platform's official API using your own (free) developer credentials. Any connected channel publishes for real; the rest stay simulated. TikTok & YouTube need video content APIs and platform audits — not available here yet.</p>
        {["meta", "x", "linkedin", "threads"].map((p) => <ProviderBlock key={p} prov={p} status={status?.[p]} onChanged={load} />)}
      </CardContent>
    </Card>
  );
}
